import PropTypes from "prop-types";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { guidelineShape } from "../../lib/data_shapes";
import { getRecommendationText, getRecommendationType } from "../../lib/helpers";
import Box from "../common/Box";
import Certainty from "../common/Certainty";
import Intents from "../common/Intents";
import GuidelineHeader from "./GuidelineHeader";
import RecommendationIcon from "../common/RecommendationIcon";
import TypeHeader, { RECOMMENDATION_FORMALITIES } from "../common/TypeHeader";
import Scores from "../common/Scores";
import InstructionModal from "../common/InstructionModal";
import Transformed from "../common/Transformed";

const RecommendationHeader = ({
  guideline,
  gradeCertaintyOfEvidence,
  informal,
  intents,
  recommendation,
  recommendationFormality,
  gps,
  transformed,
  adapted,
  adolopment,
  onRequestAdolopment,
}) => {
  const { t } = useTranslation();
  const { strength: gradeStrength, direction: recommendationDirection } = recommendation ?? {};

  const recommendationType = useMemo(() => {
    return getRecommendationType(recommendationDirection, gradeStrength);
  }, [recommendationDirection, gradeStrength]);

  const recommendationText = useMemo(() => {
    return getRecommendationText(t, gradeStrength);
  }, [t, gradeStrength]);

  const recommendationBoxBgClass = useMemo(() => {
    switch (recommendationFormality) {
      case "gps":
        return "bg-yellow-800";
      case "informal":
        return "bg-purple-800";
      case "formal":
        return "bg-teal-800";
      default:
        return "";
    }
  }, [recommendationFormality]);

  const agreeScoreBgClass = useMemo(() => {
    switch (recommendationFormality) {
      case "gps":
        return "bg-yellow-900";
      case "informal":
        return "bg-purple-900";
      case "formal":
        return "bg-teal-900";
      default:
        return "";
    }
  }, [recommendationFormality]);

  if (!recommendation) return null;

  return (
    <div>
      <div className="flex flex-row items-center">
        <div className="flex-grow">
          <TypeHeader
            adapted={adapted || adolopment}
            className="text-covid-100 mb-3 mt-1"
            recommendationFormality={recommendationFormality}
            type="recommendation"
          />
        </div>
        <div>
          <InstructionModal
            triggerLabel={
              <span className="tailwind-hidden md:block text-covid-100 hover:opacity-50">
                {t("labels.on_this_page_you_can_find")}
              </span>
            }
          >
            <h1 className="text-xl mb-6 font-semibold">{t("labels.on_this_page_you_can_find")}</h1>
            <div
              dangerouslySetInnerHTML={{ __html: t("detailsOfRecommendationPage.description") }}
            />
          </InstructionModal>
        </div>
      </div>
      <Box className={recommendationBoxBgClass}>
        <GuidelineHeader guideline={guideline} />
        <div className="flex flex-col md:flex-row">
          <div className="flex-grow p-5">
            <Intents intents={intents} />
            <div className="mr-6" dangerouslySetInnerHTML={{ __html: recommendation.value }} />
          </div>
          <div className={`flex flex-col text-covid-100 mt-3 md:mt-0 p-5 ${agreeScoreBgClass}`}>
            <div className="strength-certainty-column mb-4">
              {gradeCertaintyOfEvidence && (
                <div className="bordered-info-box">
                  <div className="flex flex-row items-center">
                    <div
                      className="font-semibold mr-4"
                      data-tip={t("tooltips.certainty_of_evidence")}
                    >
                      <div>{t("recommendation.certainty_of_evidence")}</div>
                    </div>
                    <Transformed transformed={transformed} />
                  </div>
                  <Certainty value={gradeCertaintyOfEvidence} />
                </div>
              )}
              {recommendation.strength && (
                <div className="bordered-info-box" data-tip={t("tooltips.strength")}>
                  <div className="font-semibold">{t("recommendation.recommendation_strength")}</div>
                  <div>
                    <RecommendationIcon
                      textClasses="font-normal"
                      recommendationText={recommendationText}
                      recommendationType={recommendationType}
                    />
                  </div>
                </div>
              )}
              <Scores agreeScore={guideline.agreeScore} />
              {adolopment && <div className="mt-4">{t(`adolopment.${adolopment}`)}</div>}
            </div>
            {!informal && !gps && (
              <button className="btn-gradient rounded-full py-1 mt-3" onClick={onRequestAdolopment}>
                {t("labels.requestAdolopment")}
              </button>
            )}
          </div>
        </div>
      </Box>
    </div>
  );
};

RecommendationHeader.propTypes = {
  recommendation: PropTypes.object.isRequired,
  guideline: guidelineShape.isRequired,
  gradeStrength: PropTypes.string,
  informal: PropTypes.bool,
  gps: PropTypes.bool,
  transformed: PropTypes.bool,
  intents: PropTypes.arrayOf(PropTypes.string),
  recommendationFormality: PropTypes.oneOf(RECOMMENDATION_FORMALITIES).isRequired,
};

RecommendationHeader.defaultProps = {
  gradeStrength: "",
  gps: false,
  informal: false,
  intents: [],
  transformed: false,
};

export default RecommendationHeader;
